import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Navbar from "./components/Navigation/Navbar";
import LandingPage from "./pages/LandingPage";
import CruciblePage from "./pages/CruciblePage";
import FeedbackWidget from "./components/FeedbackWidget";
import AdminUserRadar from "./components/AdminUserRadar";
import "./App.css";

import { lazy, Suspense, useEffect } from "react";

const TrackerView = lazy(() => import("./components/TrackerView"));
const Eisenboard = lazy(() => import("./components/Eisenboard"));
const InputLoop = lazy(() => import("./pages/InputLoop").then((m) => ({ default: m.InputLoop })));
const AdminConsole = lazy(() => import("./pages/AdminConsole"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
import { sendAnalyticsEvent } from "./lib/analytics";
import { readHeroSessionState, saveLastStepPath } from "./lib/sessionProgress";
import { useAuth } from "./contexts/AuthContext";

const EmailLinkHandler = () => {
  const { completeEmailLinkSignIn } = useAuth();
  useEffect(() => {
    void completeEmailLinkSignIn();
  // run once on mount — URL won't change after this
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const AppLayout = () => {
  const location = useLocation();
  const isHeroRoute = location.pathname === "/";
  const heroSession = readHeroSessionState();
  const showNavbar = !isHeroRoute || heroSession.hasResume;

  useEffect(() => {
    saveLastStepPath(location.pathname);
  }, [location.pathname]);

  return (
    <div className="flex bg-slate-950 min-h-screen text-white font-sans">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Global Navbar (Hidden on Hero until session progress exists) */}
        {showNavbar && (
          <Navbar
            heroMode={isHeroRoute}
            resumePath={heroSession.resumePath}
            trackerUnlocked={heroSession.trackerUnlocked}
            confidence={heroSession.confidence}
          />
        )}

        <div
          id="main-content"
          className={`flex-1 overflow-auto p-4 content-area ${showNavbar ? "pt-20" : "pt-0"}`}
        >
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/crucible" element={<CruciblePage />} />
              <Route path="/tracker" element={<TrackerView />} />

              <Route path="/input-loop" element={<InputLoop />} />
              <Route path="/eisenboard" element={<Eisenboard />} />
              <Route
                path="/timeline"
                element={<Navigate to="/tracker" replace />}
              />



              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={<AdminConsole />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

function App() {
  // Track page views on route changes
  const RouteTracker = () => {
    const location = useLocation();
    useEffect(() => {
      sendAnalyticsEvent("page_view");
    }, [location.pathname]);
    return null;
  };
  return (
    <AuthProvider>
      <Router>
        <RouteTracker />
        <EmailLinkHandler />
        <AppLayout />
        <AdminUserRadar />
        <FeedbackWidget />
      </Router>
    </AuthProvider>
  );
}

export default App;
